/**
 * FIFA WC 2026 group ranking (Regulations Article 13).
 * Live standings use fair play from backend; tipped standings use FIFA ranking only (no card data).
 */

import { fifaRankForTeam } from "./fifaRankings";

function effectiveScores(m) {
  const h = m.home_score;
  const a = m.away_score;
  if (h != null && a != null) return { home: h, away: a };
  if (m.status === "LIVE") return { home: 0, away: 0 };
  return null;
}

function emptyRow(code) {
  return { code, P: 0, W: 0, D: 0, L: 0, GF: 0, GA: 0, PTS: 0 };
}

export function standingsGamesPlayed(row) {
  if (row?.P != null && row.P !== "") return Number(row.P) || 0;
  return (Number(row?.W) || 0) + (Number(row?.D) || 0) + (Number(row?.L) || 0);
}

function applyResult(table, h, a, hs, aws) {
  table[h].P += 1;
  table[a].P += 1;
  table[h].GF += hs;
  table[h].GA += aws;
  table[a].GF += aws;
  table[a].GA += hs;
  if (hs > aws) {
    table[h].W += 1;
    table[h].PTS += 3;
    table[a].L += 1;
  } else if (hs < aws) {
    table[a].W += 1;
    table[a].PTS += 3;
    table[h].L += 1;
  } else {
    table[h].D += 1;
    table[a].D += 1;
    table[h].PTS += 1;
    table[a].PTS += 1;
  }
}

function buildTable(codes, results, among = null) {
  const table = Object.fromEntries(codes.map((c) => [c, emptyRow(c)]));
  const amongSet = among ? new Set(among) : null;
  for (const m of results) {
    const h = m.home_code;
    const a = m.away_code;
    const hs = m.home_score;
    const aws = m.away_score;
    if (hs == null || aws == null || !h || !a) continue;
    if (amongSet && (!amongSet.has(h) || !amongSet.has(a))) continue;
    if (table[h] && table[a]) applyResult(table, h, a, Number(hs), Number(aws));
  }
  for (const row of Object.values(table)) {
    row.GD = row.GF - row.GA;
  }
  return table;
}

function bucketByDesc(codes, table, field) {
  const buckets = new Map();
  for (const c of codes) {
    const v = table[c][field];
    if (!buckets.has(v)) buckets.set(v, []);
    buckets.get(v).push(c);
  }
  return [...buckets.keys()].sort((x, y) => y - x).map((k) => buckets.get(k));
}

function rankTiedStep3(codes) {
  if (codes.length <= 1) return [...codes];
  return [...codes].sort((a, b) => {
    const ra = fifaRankForTeam(a);
    const rb = fifaRankForTeam(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

function rankTiedStep2(codes, overall, fairPlay = null) {
  if (codes.length <= 1) return [...codes];
  for (const field of ["GD", "GF"]) {
    const buckets = bucketByDesc(codes, overall, field);
    if (buckets.length <= 1) continue;
    const best = buckets[0];
    const rest = buckets.slice(1).flat();
    if (!rest.length) continue;
    if (best.length === 1) return [...best, ...rankTiedStep2(rest, overall, fairPlay)];
  }
  if (fairPlay) {
    const fpTable = Object.fromEntries(codes.map((c) => [c, { FP: Number(fairPlay[c] ?? 0) }]));
    const buckets = bucketByDesc(codes, fpTable, "FP");
    if (buckets.length > 1) {
      const best = buckets[0];
      const rest = buckets.slice(1).flat();
      if (rest.length) {
        if (best.length === 1) return [...best, ...rankTiedStep2(rest, overall, fairPlay)];
        return rankTiedStep3(codes);
      }
    }
  }
  return rankTiedStep3(codes);
}

function rankTiedStep1(codes, results, overall, fairPlay = null) {
  if (codes.length <= 1) return [...codes];
  const h2h = buildTable(codes, results, codes);
  for (const field of ["PTS", "GD", "GF"]) {
    const buckets = bucketByDesc(codes, h2h, field);
    if (buckets.length <= 1) continue;
    const best = buckets[0];
    const rest = buckets.slice(1).flat();
    if (!rest.length) continue;
    if (best.length === 1) return [...best, ...rankTiedOnPoints(rest, results, overall, fairPlay)];
    return [...rankTiedStep1(best, results, overall, fairPlay), ...rankTiedOnPoints(rest, results, overall, fairPlay)];
  }
  return rankTiedStep2(codes, overall, fairPlay);
}

function rankTiedOnPoints(codes, results, overall, fairPlay = null) {
  return rankTiedStep1(codes, results, overall, fairPlay);
}

export function rankGroupCodes(codes, results, fairPlay = null) {
  const list = [...codes];
  const overall = buildTable(list, results, null);
  const byPts = new Map();
  for (const c of list) {
    const p = overall[c].PTS;
    if (!byPts.has(p)) byPts.set(p, []);
    byPts.get(p).push(c);
  }
  const order = [];
  for (const pts of [...byPts.keys()].sort((a, b) => b - a)) {
    const bucket = byPts.get(pts);
    if (bucket.length === 1) order.push(bucket[0]);
    else order.push(...rankTiedOnPoints(bucket, results, overall, fairPlay));
  }
  return order;
}

/**
 * Build standings rows from group match results (finished or tipped).
 * @param {string[]} codes - team codes in the group
 * @param {{ home_code, away_code, home_score, away_score }[]} results
 * @param {object|null} [fairPlay] - optional { teamCode: score } from live API
 */
export function calcGroupStandingsFromResults(codes, results, fairPlay = null) {
  const overall = buildTable(codes, results, null);
  const order = rankGroupCodes(codes, results, fairPlay);
  return order.map((code, i) => ({ ...overall[code], rank: i + 1 }));
}

/**
 * @param {object[]} fixtures - project fixtures (GROUP stage)
 * @param {object} groups - { groupLetter: [codes] }
 * @param {object} [betsByMatchId] - if set, use tipped scores for all GROUP matches with both scores
 * @param {object|null} [fairPlay] - live fair play totals (backend only)
 */
export function calcGroupStandingsFromFixtures(fixtures, groups, betsByMatchId = null, fairPlay = null) {
  const out = {};
  for (const [g, codes] of Object.entries(groups || {})) {
    const results = [];
    for (const m of fixtures || []) {
      if (m.stage !== "GROUP" || m.group !== g) continue;
      let hs = null;
      let aws = null;
      if (betsByMatchId) {
        const b = betsByMatchId[m.id];
        if (!b || b.home_score == null || b.home_score === undefined || b.away_score == null || b.away_score === undefined) continue;
        hs = b.home_score;
        aws = b.away_score;
      } else {
        if (m.status !== "FINISHED" && m.status !== "LIVE") continue;
        const eff = effectiveScores(m);
        if (!eff) continue;
        hs = eff.home;
        aws = eff.away;
      }
      if (hs == null || aws == null || !m.home_code || !m.away_code) continue;
      results.push({
        home_code: m.home_code,
        away_code: m.away_code,
        home_score: hs,
        away_score: aws,
      });
    }
    const fp = betsByMatchId ? null : fairPlay;
    out[g] = calcGroupStandingsFromResults(codes, results, fp);
  }
  return out;
}

/** FIFA Art. 13 — rank third-placed teams across groups (positions 1–8 qualify). */
export function calcBestThirdFromGroups(groupStandings, fairPlay = null) {
  const fp = fairPlay || {};
  const thirds = [];
  for (const g of Object.keys(groupStandings || {}).sort()) {
    const rows = groupStandings[g] || [];
    if (rows.length < 3) continue;
    thirds.push({ ...rows[2], _group: g });
  }
  thirds.sort((a, b) => {
    const key = (row) => {
      const code = String(row.code || "");
      const rk = fifaRankForTeam(code);
      return [
        -(row.PTS || 0),
        -(row.GD || 0),
        -(row.GF || 0),
        -(Number(fp[code] ?? 0)),
        rk,
        String(row._group || ""),
      ];
    };
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] !== kb[i]) return ka[i] < kb[i] ? -1 : 1;
    }
    return 0;
  });
  return thirds.map((row, i) => {
    const { _group, ...rest } = row;
    return { ...rest, third_rank: i + 1, qualified: i < 8 };
  });
}

/** Normalize /standings API payload (legacy flat groups or { groups, best_third }). */
export function parseStandingsResponse(data) {
  if (!data || typeof data !== "object") return { groups: {}, bestThird: [] };
  if (data.groups && typeof data.groups === "object") {
    return { groups: data.groups, bestThird: Array.isArray(data.best_third) ? data.best_third : [] };
  }
  return { groups: data, bestThird: calcBestThirdFromGroups(data) };
}
